import { useState } from 'react';

import { cn } from '@apps/web/lib/utils';
import { type OrgClientRead } from '@libraries/typescript/api_client';

type ClientPreference = NonNullable<
    OrgClientRead['caregiver_client_preferences']
>[number];

export type EntityPreferenceRow = {
    readonly entityId: string;
    // Name of the counterpart entity: the caregiver on a client's
    // memory tab, the client on a caregiver's memory tab.
    readonly name: string;
    readonly preference_value: ClientPreference['preference_value'];
    readonly origin: ClientPreference['origin'];
    readonly source: ClientPreference['source'];
};

// Names shown per preference group before collapsing behind "+N more".
const VISIBLE_NAMES_PER_GROUP = 8;

// Severity order for display: the blocked list is the one schedulers
// check before dispatching, so it always comes first.
const GROUP_ORDER = ['do_not_schedule', 'prefer_not', 'prefer'] as const;

const PREFERENCE_VALUE_LABELS: Record<(typeof GROUP_ORDER)[number], string> = {
    do_not_schedule: 'Do not schedule',
    prefer_not: 'Prefer not',
    prefer: 'Preferred',
};

const PREFERENCE_VALUE_CLASSES: Record<(typeof GROUP_ORDER)[number], string> = {
    do_not_schedule: 'text-red-800',
    prefer_not: 'text-amber-800',
    prefer: 'text-green-800',
};

const PREFERENCE_ORIGIN_DETAIL_LABELS: Record<
    ClientPreference['origin'],
    string
> = {
    external: 'imported from the EHR',
    sms: 'told Phoebe over SMS',
    phone: 'told Phoebe on a call',
    agent_note: 'taught to Phoebe in a note',
    other: 'recorded manually',
};

const PREFERENCE_SOURCE_LABELS: Record<ClientPreference['source'], string> = {
    caregiver: 'Caregiver',
    client: 'Client',
};

type EntityPreferencesSectionProps = {
    readonly title: string;
    readonly rows: ReadonlyArray<EntityPreferenceRow>;
    // Display name of the EHR ("AxisCare", "WellSky", …) for imported
    // rows; falls back to a generic "Imported" when unknown.
    readonly importedSourceLabel?: string;
};

function rowTooltip(
    row: EntityPreferenceRow,
    importedSourceLabel: string | undefined,
): string {
    const origin =
        row.origin === 'external' && importedSourceLabel
            ? `imported from ${importedSourceLabel}`
            : PREFERENCE_ORIGIN_DETAIL_LABELS[row.origin];
    return `${PREFERENCE_SOURCE_LABELS[row.source]} preference, ${origin}`;
}

/**
 * Compact read-only table of caregiver↔client preferences rendered inside
 * the memory card: one row per preference value, with the counterpart
 * names listed in the row. Neutral "no preference" rows are hidden; the
 * section renders nothing when no meaningful rows remain.
 */
export function EntityPreferencesSection({
    title,
    rows,
    importedSourceLabel,
}: EntityPreferencesSectionProps) {
    const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(
        new Set(),
    );

    // Names are not identities. Keep each person's claims grouped by value
    // so display deduplication and conflict detection share the same rows.
    const rowsByEntity = new Map<
        string,
        Map<ClientPreference['preference_value'], Array<EntityPreferenceRow>>
    >();
    for (const row of rows) {
        if (row.preference_value === 'no_preference') {
            continue;
        }
        const rowsByValue =
            rowsByEntity.get(row.entityId) ??
            new Map<
                ClientPreference['preference_value'],
                Array<EntityPreferenceRow>
            >();
        const matchingRows = rowsByValue.get(row.preference_value) ?? [];
        matchingRows.push(row);
        rowsByValue.set(row.preference_value, matchingRows);
        rowsByEntity.set(row.entityId, rowsByValue);
    }
    const groups = GROUP_ORDER.map((value) => ({
        value,
        rows: Array.from(rowsByEntity.values())
            .flatMap((rowsByValue) => {
                const matchingRows = rowsByValue.get(value);
                if (!matchingRows) {
                    return [];
                }
                const row = matchingRows[0]!;
                const sourceLabels = matchingRows.map((entry) => {
                    const origin =
                        entry.origin === 'external'
                            ? (importedSourceLabel ?? 'Imported')
                            : entry.origin === 'other'
                              ? 'Manual'
                              : 'Phoebe';
                    return `${PREFERENCE_SOURCE_LABELS[entry.source]} · ${origin}`;
                });
                return [
                    {
                        entityId: row.entityId,
                        name: row.name,
                        displayName:
                            rowsByValue.size > 1
                                ? `${row.name} (${Array.from(new Set(sourceLabels)).join('; ')})`
                                : row.name,
                        tooltip: Array.from(
                            new Set(
                                matchingRows.map((entry) =>
                                    rowTooltip(entry, importedSourceLabel),
                                ),
                            ),
                        ).join('\n'),
                    },
                ];
            })
            .sort((a, b) => a.name.localeCompare(b.name)),
    }));

    if (groups.every((group) => group.rows.length === 0)) {
        return null;
    }

    const toggleGroup = (value: string) => {
        const next = new Set(expandedGroups);
        if (next.has(value)) {
            next.delete(value);
        } else {
            next.add(value);
        }
        setExpandedGroups(next);
    };

    return (
        <div className="border-border overflow-hidden rounded-md border">
            <table className="w-full table-fixed text-xs">
                <thead>
                    <tr className="bg-muted/50 border-border border-b">
                        <th
                            colSpan={3}
                            className="text-muted-foreground px-2.5 py-1.5 text-center text-xs font-semibold"
                        >
                            {title}
                        </th>
                    </tr>
                    <tr className="bg-muted/50 border-border border-b">
                        {groups.map((group) => (
                            <th
                                key={group.value}
                                className={cn(
                                    'border-border w-1/3 border-r px-2.5 py-1 text-left font-medium last:border-r-0',
                                    PREFERENCE_VALUE_CLASSES[group.value],
                                )}
                            >
                                {PREFERENCE_VALUE_LABELS[group.value]}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {groups.map((group) => {
                            const isExpanded = expandedGroups.has(group.value);
                            const visibleRows = isExpanded
                                ? group.rows
                                : group.rows.slice(0, VISIBLE_NAMES_PER_GROUP);
                            const hiddenCount =
                                group.rows.length - visibleRows.length;
                            return (
                                <td
                                    key={group.value}
                                    className="border-border border-r px-2.5 py-1.5 align-top last:border-r-0"
                                >
                                    {visibleRows.map((row) => (
                                        <div
                                            key={row.entityId}
                                            className="break-words"
                                            title={row.tooltip}
                                        >
                                            {row.displayName}
                                        </div>
                                    ))}
                                    {(hiddenCount > 0 || isExpanded) && (
                                        <button
                                            type="button"
                                            className="text-muted-foreground hover:text-foreground cursor-pointer"
                                            onClick={() =>
                                                toggleGroup(group.value)
                                            }
                                        >
                                            {isExpanded
                                                ? 'Show less'
                                                : `+${hiddenCount} more`}
                                        </button>
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
